import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Sale } from '../../models/kiosk.models';
import { SalesService } from '../../services/sales.service';
import { LogService } from '../../../services/log.service';

interface ServiceGroup {
  scheduledServiceId: string;
  serviceName: string;
  startTime: string;
  endTime: string;
  date: Date;
  status: string;
  expectedRevenue: number;
  actualRevenue: number;
  salesCount: number;
  sales: Sale[];
}

@Component({
  selector: 'app-history-view',
  imports: [CommonModule],
  templateUrl: './history-view.component.html',
  styleUrls: ['./history-view.component.css'],
})
export class HistoryViewComponent implements OnInit {
  private log = inject(LogService);
  serviceGroups: ServiceGroup[] = [];
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  expandedServices = new Map<string, boolean>(); // Track l'état des accordéons

  constructor(private salesService: SalesService) {}

  ngOnInit(): void {
    this.loadSalesHistory();
    this.setupAutoRefresh();
  }

  private setupAutoRefresh(): void {
    // S'abonner aux notifications SSE push depuis le backend
    // Le SSE se déclenche quand N'IMPORTE QUEL client crée une vente
    this.salesService.salesChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.log.debug('🔄 Rafraîchissement historique suite à notification SSE');
        this.loadSalesHistory();
      });
  }

  private loadSalesHistory(): void {
    this.log.debug("📥 Chargement de l'historique...");
    this.salesService
      .getSalesByService(undefined, undefined, 30)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((groups) => {
        this.log.debug('📥 Données reçues:', groups.length, 'groupes');
        this.serviceGroups = groups;
        this.log.debug('✅ Vue mise à jour');
        this.cdr.markForCheck();
      });
  }

  getServiceIcon(serviceName: string): string {
    const icons: { [key: string]: string } = {
      'Petit-déjeuner': '🌅',
      Déjeuner: '☀️',
      Dîner: '🌙',
    };
    return icons[serviceName] || '🍽️';
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  getTotalProducts(group: ServiceGroup): number {
    return group.sales.reduce(
      (sum, sale) => sum + sale.items.reduce((s, item) => s + item.quantity, 0),
      0,
    );
  }

  getAverageRevenue(group: ServiceGroup): number {
    return group.salesCount > 0 ? group.actualRevenue / group.salesCount : 0;
  }

  toggleService(serviceId: string): void {
    const currentState = this.expandedServices.get(serviceId) || false;
    this.expandedServices.set(serviceId, !currentState);
  }

  isServiceExpanded(serviceId: string): boolean {
    return this.expandedServices.get(serviceId) || false;
  }
}
